import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Share2, Truck, ShieldCheck, ChevronDown, Star } from "lucide-react";
import api, { currency } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { add } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ show: false, x: 0, y: 0 });
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${slug}`);
      setProduct(data);
      setActiveImg(0);
      const [rel, rev] = await Promise.all([api.get(`/products/${slug}/related`), api.get(`/reviews/${data.id}`)]);
      setRelated(rel.data);
      setReviews(rev.data);
    })().catch(() => toast.error("Product not found"));
  }, [slug]);

  if (!product) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24"><div className="h-96 bg-ivory animate-pulse" /></div>;

  const inWishlist = user?.wishlist?.includes(product.id);
  const toggleWishlist = async () => {
    if (!user) { toast("Sign in to save favourites"); return; }
    try {
      if (inWishlist) await api.delete(`/wishlist/${product.id}`);
      else await api.post(`/wishlist/${product.id}`);
      await refresh();
    } catch {}
  };

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: product.name, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
    } catch {}
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to review"); return; }
    try {
      await api.post("/reviews", { product_id: product.id, ...reviewForm });
      const { data } = await api.get(`/reviews/${product.id}`);
      setReviews(data);
      setReviewForm({ rating: 5, title: "", comment: "" });
      toast.success("Thank you for your review");
    } catch (e) { toast.error("Please try again"); }
  };

  return (
    <div data-testid="product-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs text-charcoal/50 mb-8">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <Link to={`/category/${product.category}`}>{product.category.replace("-", " ")}</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-12 lg:gap-20">
        {/* Gallery */}
        <div className="space-y-4">
          <div
            className="zoom-frame relative w-full aspect-[4/5] bg-ivory"
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
            }}
            onMouseLeave={() => setZoom({ show: false, x: 0, y: 0 })}
          >
            <img
              src={product.images?.[activeImg]}
              alt={product.name}
              className="zoom-target w-full h-full object-cover"
              style={zoom.show ? { transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
              data-testid="product-main-image"
            />
          </div>
          <div className="flex gap-3">
            {product.images?.map((im, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`thumb-${i}`}
                className={`w-20 h-24 border ${i === activeImg ? "border-emerald" : "border-transparent"} img-hover`}
              >
                <img src={im} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="overline text-emerald mb-3">{product.category.replace("-", " ")}</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]" data-testid="product-name">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex text-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating_avg || 0) ? "fill-gold" : ""}`} />
              ))}
            </div>
            <div className="text-xs text-charcoal/60">{product.rating_count || 0} review{product.rating_count === 1 ? "" : "s"}</div>
          </div>

          <div className="mt-6 flex items-baseline gap-4" data-testid="product-price">
            <div className="font-serif text-3xl">{currency(product.price)}</div>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <div className="text-charcoal/40 line-through">{currency(product.compare_at_price)}</div>
            )}
          </div>

          <p className="mt-8 text-charcoal/75 leading-relaxed text-[15px]">{product.description}</p>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border divider-gold h-12">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-full text-lg">−</button>
              <div className="w-10 text-center text-sm" data-testid="qty-value">{qty}</div>
              <button data-testid="qty-inc" onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="w-11 h-full text-lg">+</button>
            </div>
            <Button
              data-testid="add-to-cart"
              onClick={() => { add(product, qty); toast.success("Added to cart"); }}
              disabled={product.stock === 0}
              className="flex-1 h-12 rounded-none bg-charcoal text-ivory hover:bg-charcoal/85 tracking-[0.15em] uppercase text-xs"
            >
              {product.stock === 0 ? "Sold out" : "Add to cart"}
            </Button>
          </div>

          <Button
            data-testid="buy-now"
            onClick={() => { add(product, qty); nav("/checkout"); }}
            disabled={product.stock === 0}
            className="mt-3 w-full h-12 rounded-none bg-emerald text-ivory hover:bg-emerald/90 tracking-[0.15em] uppercase text-xs"
          >
            Buy now
          </Button>

          <Link to="/custom-order" data-testid="request-custom" className="mt-3 block text-center border divider-gold py-3 hover:border-emerald hover:text-emerald overline text-charcoal">
            Or · Request a similar piece, made for you
          </Link>

          <div className="mt-6 flex items-center gap-6 text-xs text-charcoal/70">
            <button data-testid="wishlist-btn" onClick={toggleWishlist} className="inline-flex items-center gap-2 hover:text-emerald">
              <Heart className={`w-4 h-4 ${inWishlist ? "fill-emerald text-emerald" : ""}`} /> Wishlist
            </button>
            <button onClick={share} className="inline-flex items-center gap-2 hover:text-emerald"><Share2 className="w-4 h-4" /> Share</button>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4 text-xs text-charcoal/70">
            <div className="flex items-start gap-2"><Truck className="w-4 h-4 mt-0.5 text-emerald" /> <div><div className="text-charcoal">Delivery</div>{product.delivery_estimate}</div></div>
            <div className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-emerald" /> <div><div className="text-charcoal">Craft warranty</div>Lifetime hardware</div></div>
          </div>

          <Accordion type="single" collapsible className="mt-10 divider-gold border-t border-b" defaultValue="specs">
            <AccordionItem value="specs" className="border-b divider-gold">
              <AccordionTrigger className="uppercase tracking-widest text-xs">Materials & dimensions</AccordionTrigger>
              <AccordionContent className="text-sm text-charcoal/70">
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="text-charcoal/50">Materials</div><div>{(product.materials || []).join(", ") || "—"}</div>
                  <div className="text-charcoal/50">Dimensions</div><div>{product.dimensions || "—"}</div>
                  <div className="text-charcoal/50">Availability</div><div>{product.stock > 0 ? `${product.stock} in stock` : "Sold out"}</div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-b-0">
              <AccordionTrigger className="uppercase tracking-widest text-xs">Care</AccordionTrigger>
              <AccordionContent className="text-sm text-charcoal/70">
                Store in the linen pouch it arrived in. Keep away from direct sunlight and perfume. For leather, condition with our balm every 6 months. For pearls, wipe softly after wear.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-16">
          <div>
            <div className="overline text-emerald mb-3">Voices</div>
            <h2 className="font-serif text-4xl tracking-tight mb-10">What people are saying</h2>
            {reviews.length === 0 ? (
              <div className="text-charcoal/50 italic">Be the first to leave a review.</div>
            ) : (
              <div className="space-y-10">
                {reviews.map((r) => (
                  <div key={r.id} className="border-t divider-gold pt-6">
                    <div className="flex items-center gap-3">
                      <div className="flex text-gold">
                        {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-gold" : ""}`} />)}
                      </div>
                      {r.verified_purchase && <span className="overline text-[9px] text-emerald">Verified purchase</span>}
                    </div>
                    {r.title && <div className="font-serif text-xl mt-3">{r.title}</div>}
                    <p className="text-sm text-charcoal/75 mt-2 leading-relaxed">{r.comment}</p>
                    <div className="text-xs text-charcoal/50 mt-3">— {r.user_name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={submitReview} className="bg-ivory/60 p-8" data-testid="review-form">
            <div className="overline text-emerald mb-3">Write a review</div>
            <div className="mb-4">
              <div className="text-xs text-charcoal/60 mb-2">Rating</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`rating-${n}`}>
                    <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "fill-gold text-gold" : "text-charcoal/30"}`} />
                  </button>
                ))}
              </div>
            </div>
            <Input placeholder="Title" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} data-testid="review-title" className="mb-3 bg-cream" />
            <Textarea placeholder="Share your thoughts" required value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} data-testid="review-comment" className="mb-3 bg-cream min-h-[100px]" />
            <Button data-testid="submit-review" type="submit" className="w-full rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Submit review</Button>
            {!user && <p className="text-xs text-charcoal/50 mt-3 text-center">Sign in required to submit a review.</p>}
          </form>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-32">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">You may also like</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-12">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
